(() => {
  const CUSTOM_VALUE = 'Customized Size';
  const STORAGE_VERSION = 'v3';

  function initCustomSize(root) {
    if (!root || root.dataset.customSizeInitialized === 'true') {
      return;
    }

    const formId = root.dataset.formId;
    const form = document.getElementById(formId);
    const modal = root.querySelector('x-modal');
    const saveButton = root.querySelector('[data-custom-save]');

    if (!formId || !form || !modal || !saveButton) {
      return;
    }

    root.dataset.customSizeInitialized = 'true';

    const requiredFields = [
      'Height',
      'Weight',
      'Chest',
      'Jacket Waist',
      'Shoulder',
      'Sleeve Length',
      'Jacket Length',
      'Trouser Waist',
      'Hip',
      'Thigh',
      'Inseam'
    ];

    let selectedUnit = 'in';
    let currentVariantIsCustomized = false;

    /*
     * ============================================================
     * STORAGE
     * ============================================================
     */

    function getStorageKey() {
      return `moranx-custom-size-${STORAGE_VERSION}-${formId}`;
    }

    function readStorage() {
      try {
        const raw = sessionStorage.getItem(getStorageKey());

        if (!raw) return null;

        const data = JSON.parse(raw);

        if (!data || data.saved !== true || !data.values) {
          return null;
        }

        return data;
      } catch (error) {
        return null;
      }
    }

    function writeStorage(data) {
      try {
        sessionStorage.setItem(
          getStorageKey(),
          JSON.stringify(data)
        );

        return true;
      } catch (error) {
        return false;
      }
    }

    function clearStorage() {
      try {
        sessionStorage.removeItem(getStorageKey());
      } catch (error) {
        // Ignore.
      }
    }

    function measurementsAreSaved() {
      return readStorage() !== null;
    }

    /*
     * ============================================================
     * BASIC DOM
     * ============================================================
     */

    function getField(name) {
      return modal.querySelector(
        `[data-custom-field="${CSS.escape(name)}"]`
      );
    }

    function getFields() {
      return Array.from(
        modal.querySelectorAll('[data-custom-field]')
      );
    }

    function getProperty(name) {
      return root.querySelector(
        `[data-property="${CSS.escape(name)}"]`
      );
    }

    function getProperties() {
      return Array.from(
        root.querySelectorAll('[data-property]')
      );
    }

    function getConfirm() {
      return modal.querySelector('[data-custom-confirm]');
    }

    function getError() {
      return modal.querySelector('[data-custom-error]');
    }

    function getStatus() {
      return root.querySelector('[data-custom-size-status]');
    }

    /*
     * ============================================================
     * MODAL
     * ============================================================
     */

    function openModal() {
      if (typeof modal.show === 'function') {
        modal.show();
      }
    }

    function closeModal() {
      if (typeof modal.hide === 'function') {
        modal.hide();
      }
    }

    /*
     * ============================================================
     * STATUS
     * ============================================================
     */

    function showSavedStatus() {
      const status = getStatus();

      if (!status) return;

      status.hidden = false;

      const spans = status.querySelectorAll('span');

      if (spans.length > 1) {
        spans[1].textContent = 'Custom measurements saved';
      }
    }

    function hideSavedStatus() {
      const status = getStatus();

      if (status) {
        status.hidden = true;
      }
    }

    /*
     * ============================================================
     * ERROR
     * ============================================================
     */

    function clearError() {
      const error = getError();

      if (!error) return;

      error.hidden = true;
      error.textContent = '';
    }

    function showError(message) {
      const error = getError();

      if (!error) return;

      error.textContent = message;
      error.hidden = false;
    }

    /*
     * ============================================================
     * UNIT
     * ============================================================
     */

    function updateUnitLabels() {
      const lengthText =
        selectedUnit === 'cm' ? '(cm)' : '(in)';

      const weightText =
        selectedUnit === 'cm' ? '(kg)' : '(lb)';

      modal
        .querySelectorAll('[data-length-unit]')
        .forEach(element => {
          element.textContent = lengthText;
        });

      modal
        .querySelectorAll('[data-weight-unit]')
        .forEach(element => {
          element.textContent = weightText;
        });

      modal
        .querySelectorAll('[data-custom-unit]')
        .forEach(button => {
          button.classList.toggle(
            'is-active',
            button.dataset.customUnit === selectedUnit
          );
        });
    }

    /*
     * ============================================================
     * SHOPIFY PROPERTIES
     * ============================================================
     */

    function setPropertiesEnabled(enabled) {
      getProperties().forEach(input => {
        input.disabled = !enabled;
      });
    }

    function clearPropertyValues() {
      getProperties().forEach(input => {
        input.value = '';
        input.disabled = true;
      });
    }

    function writeDataToProperties(data) {
      if (!data || !data.values) return;

      const sizeType = getProperty('Size Type');
      const measurementUnit = getProperty('Measurement Unit');

      if (sizeType) {
        sizeType.value = CUSTOM_VALUE;
      }

      if (measurementUnit) {
        measurementUnit.value =
          data.unit === 'cm'
            ? 'Centimeters'
            : 'Inches';
      }

      Object.entries(data.values).forEach(([name, rawValue]) => {
        const property = getProperty(name);

        if (!property) return;

        const value = String(rawValue ?? '').trim();

        if (!value) {
          property.value = '';
          return;
        }

        if (name === 'Measurement Notes') {
          property.value = value;
          return;
        }

        if (name === 'Weight') {
          property.value =
            `${value} ${data.unit === 'cm' ? 'kg' : 'lb'}`;

          return;
        }

        property.value =
          `${value} ${data.unit === 'cm' ? 'cm' : 'in'}`;
      });
    }

    function enableSavedProperties() {
      const data = readStorage();

      if (!data) {
        setPropertiesEnabled(false);
        return false;
      }

      writeDataToProperties(data);
      setPropertiesEnabled(true);

      return true;
    }

    /*
     * ============================================================
     * RESTORE
     * ============================================================
     */

    function restoreModalFromStorage() {
      const data = readStorage();

      if (!data) return false;

      selectedUnit =
        data.unit === 'cm' ? 'cm' : 'in';

      updateUnitLabels();

      Object.entries(data.values).forEach(([name, value]) => {
        const field = getField(name);

        if (!field) return;

        field.value = value;
        field.removeAttribute('aria-invalid');
      });

      const confirm = getConfirm();

      if (confirm) {
        confirm.checked = true;
      }

      clearError();

      return true;
    }

    /*
     * ============================================================
     * VALIDATION
     * ============================================================
     */

    function getMissingFields() {
      const missing = [];

      requiredFields.forEach(name => {
        const field = getField(name);

        const value =
          field
            ? String(field.value || '').trim()
            : '';

        if (!value) {
          missing.push(name);

          if (field) {
            field.setAttribute('aria-invalid', 'true');
          }
        } else if (field) {
          field.removeAttribute('aria-invalid');
        }
      });

      return missing;
    }

    /*
     * ============================================================
     * SAVE
     * ============================================================
     */

    function saveMeasurements() {
      clearError();

      const missing = getMissingFields();

      if (missing.length > 0) {
        showError(
          `Please complete: ${missing.join(', ')}.`
        );

        const first = getField(missing[0]);

        if (first) {
          first.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });

          setTimeout(() => first.focus(), 250);
        }

        return;
      }

      const confirm = getConfirm();

      if (!confirm || !confirm.checked) {
        showError(
          'Please confirm that you have checked your measurements and that they are accurate.'
        );

        return;
      }

      const values = {};

      getFields().forEach(field => {
        const name = field.dataset.customField;

        if (!name) return;

        values[name] =
          String(field.value || '').trim();
      });

      const data = {
        saved: true,
        unit: selectedUnit,
        values
      };

      const success = writeStorage(data);

      if (!success || !readStorage()) {
        showError(
          'Your measurements could not be saved. Please try again.'
        );

        return;
      }

      /*
       * Write properties BEFORE closing.
       */
      writeDataToProperties(data);
      setPropertiesEnabled(true);

      currentVariantIsCustomized = true;

showSavedStatus();
clearError();

closeModal();

/*
 * Prestige may replace the Customized Size DOM
 * immediately after the modal closes.
 *
 * Restore the saved message on the newly rendered
 * custom-size component.
 */
const restoreSavedMessage = () => {
  document
    .querySelectorAll('[data-custom-size-root]')
    .forEach(newRoot => {
      if (newRoot.dataset.formId !== formId) {
        return;
      }

      const status = newRoot.querySelector(
        '[data-custom-size-status]'
      );

      if (!status) {
        return;
      }

      status.hidden = false;

      const spans = status.querySelectorAll('span');

      if (spans.length > 1) {
        spans[1].textContent =
          'Custom measurements saved';
      }
    });
};

/*
 * Prestige's rerender timing is asynchronous,
 * so check the new DOM a few times.
 */
window.setTimeout(restoreSavedMessage, 100);
window.setTimeout(restoreSavedMessage, 300);
window.setTimeout(restoreSavedMessage, 600);
window.setTimeout(restoreSavedMessage, 1000);
    }

    /*
     * ============================================================
     * CUSTOMIZED SIZE DETECTION
     * ============================================================
     */

    function isCustomizedVariant(variant) {
      if (!variant) return false;

      return [
        variant.option1,
        variant.option2,
        variant.option3
      ]
        .filter(Boolean)
        .some(value => {
          return String(value).trim() === CUSTOM_VALUE;
        });
    }

    function readCurrentVariant() {
      const picker = document.querySelector(
        `variant-picker[form-id="${CSS.escape(formId)}"]`
      );

      if (!picker) return null;

      const json = picker.querySelector('script[data-variant]');

      if (!json) return null;

      try {
        return JSON.parse(json.textContent);
      } catch (error) {
        return null;
      }
    }

    /*
     * ============================================================
     * DYNAMIC CHECKOUT
     * ============================================================
     */

    function setDynamicCheckoutVisibility(isCustomized) {
      const paymentButton =
        form.querySelector('.shopify-payment-button');

      if (paymentButton) {
        paymentButton.style.display =
          isCustomized ? 'none' : '';
      }

      document.body.classList.toggle(
        'mx-custom-size-selected',
        isCustomized
      );
    }

    /*
     * ============================================================
     * FIND CUSTOMIZED SIZE CONTROL
     *
     * Prestige structure:
     *
     * input.sr-only[type=radio]
     * +
     * label.block-swatch
     *   span Customized Size
     *
     * IMPORTANT:
     * input.value is Shopify option_value.id,
     * NOT "Customized Size".
     * Therefore we identify the visible label text.
     * ============================================================
     */

    function findCustomizedSizeLabel() {
      const picker = root.closest('variant-picker');

      if (!picker) return null;

      const labels =
        Array.from(
          picker.querySelectorAll('label.block-swatch')
        );

      return (
        labels.find(label => {
          return (
            label.textContent.trim() === CUSTOM_VALUE
          );
        }) || null
      );
    }

    /*
     * ============================================================
     * CUSTOMER CLICK → OPEN MODAL
     *
     * THIS is now the ONLY automatic opening path.
     * ============================================================
     */

    const customizedLabel = findCustomizedSizeLabel();

    if (customizedLabel) {
      customizedLabel.addEventListener(
        'click',
        () => {
          /*
           * Let Prestige process the radio selection first.
           */
          window.setTimeout(() => {
            currentVariantIsCustomized = true;

            setDynamicCheckoutVisibility(true);

            /*
             * Existing measurements:
             * do NOT automatically open.
             */
            if (measurementsAreSaved()) {
              restoreModalFromStorage();
              enableSavedProperties();
              showSavedStatus();

              return;
            }

            /*
             * No saved measurements:
             * this was a REAL customer click,
             * so open the modal.
             */
            setPropertiesEnabled(false);
            hideSavedStatus();

            openModal();
          }, 80);
        }
      );
    }

    /*
     * ============================================================
     * EDIT MEASUREMENTS
     * ============================================================
     */

    const editButton =
      root.querySelector('.mx-custom-size__edit');

    if (editButton) {
      /*
       * x-modal already understands aria-controls.
       *
       * We do NOT need to manually call openModal here.
       * Prestige handles the button.
       *
       * Restore values just before customer sees it.
       */
      editButton.addEventListener(
        'click',
        () => {
          restoreModalFromStorage();
        }
      );
    }

    /*
     * ============================================================
     * UNIT BUTTONS
     * ============================================================
     */

    modal.addEventListener(
      'click',
      event => {
        const button =
          event.target.closest('[data-custom-unit]');

        if (!button) return;

        const newUnit =
          button.dataset.customUnit;

        if (!newUnit || newUnit === selectedUnit) {
          return;
        }

      /*
 * Convert measurements before updating the unit.
 * Length: inches <-> centimeters
 * Weight: pounds <-> kilograms
 * Keep notes and empty fields unchanged.
 */
getFields().forEach(field => {
  if (field.type !== 'number') return;

  const rawValue = field.value.trim();

  if (rawValue === '') return;

  const value = Number(rawValue);

  if (!Number.isFinite(value)) return;

  const factor =
    field.dataset.customField === 'Weight'
      ? 0.45359237
      : 2.54;

  const convertedValue =
    newUnit === 'cm'
      ? value * factor
      : value / factor;

  field.value = String(
    Math.round(convertedValue * 10) / 10
  );

  field.removeAttribute('aria-invalid');
});

selectedUnit = newUnit;

        const confirm = getConfirm();

        if (confirm) {
          confirm.checked = false;
        }

        clearStorage();
        clearPropertyValues();
        hideSavedStatus();
        clearError();

        updateUnitLabels();
      }
    );

    /*
     * ============================================================
     * EDITING A FIELD INVALIDATES SAVED STATE
     * ============================================================
     */

    modal.addEventListener(
      'input',
      event => {
        const field =
          event.target.closest('[data-custom-field]');

        if (!field) return;

        field.removeAttribute('aria-invalid');

        /*
         * Once customer changes a saved value,
         * they must Save Measurements again.
         */
        if (measurementsAreSaved()) {
          clearStorage();
          clearPropertyValues();
          hideSavedStatus();
        }

        clearError();
      }
    );

    /*
     * ============================================================
     * CONFIRM
     * ============================================================
     */

    modal.addEventListener(
      'change',
      event => {
        const checkbox =
          event.target.closest('[data-custom-confirm]');

        if (!checkbox) return;

        if (
          measurementsAreSaved() &&
          !checkbox.checked
        ) {
          clearStorage();
          clearPropertyValues();
          hideSavedStatus();
        }

        clearError();
      }
    );

    /*
     * ============================================================
     * SAVE BUTTON
     * ============================================================
     */

    saveButton.addEventListener(
      'click',
      event => {
        event.preventDefault();
        event.stopPropagation();

        saveMeasurements();
      }
    );

    /*
     * ============================================================
     * PRESTIGE VARIANT CHANGE
     *
     * CRITICAL RULE:
     *
     * THIS EVENT NEVER OPENS THE MODAL.
     * ============================================================
     */

    form.addEventListener(
      'variant:change',
      event => {
        const variant = event.detail?.variant;

        currentVariantIsCustomized =
          isCustomizedVariant(variant);

        setDynamicCheckoutVisibility(
          currentVariantIsCustomized
        );

        /*
         * Standard size.
         */
        if (!currentVariantIsCustomized) {
          setPropertiesEnabled(false);
          hideSavedStatus();

          return;
        }

        /*
         * Customized Size.
         *
         * Restore if saved.
         * If not saved, simply wait.
         *
         * DO NOT OPEN MODAL HERE.
         */
        if (measurementsAreSaved()) {
          restoreModalFromStorage();
          enableSavedProperties();
          showSavedStatus();
        } else {
          setPropertiesEnabled(false);
          hideSavedStatus();
        }
      }
    );

    /*
     * ============================================================
     * ADD TO CART SAFETY
     *
     * This is the SECOND legitimate modal-opening path.
     * ============================================================
     */

    form.addEventListener(
      'submit',
      event => {
        let customized =
          currentVariantIsCustomized;

        const variant = readCurrentVariant();

        if (variant) {
          customized =
            isCustomizedVariant(variant);

          currentVariantIsCustomized =
            customized;
        }

        setDynamicCheckoutVisibility(customized);

        /*
         * Standard size.
         */
        if (!customized) {
          setPropertiesEnabled(false);
          return;
        }

        /*
         * Customized Size without saved measurements.
         */
        if (!measurementsAreSaved()) {
          event.preventDefault();
          event.stopImmediatePropagation();

          setPropertiesEnabled(false);
          hideSavedStatus();

          openModal();

          setTimeout(() => {
            showError(
              'Please complete and save your measurements before adding this customized size to your cart.'
            );
          }, 100);

          return;
        }

        /*
         * Customized Size with saved measurements.
         */
        restoreModalFromStorage();
        enableSavedProperties();
        showSavedStatus();
      },
      true
    );

    /*
     * ============================================================
     * INITIAL STATE
     *
     * IMPORTANT:
     * Initial state NEVER opens the modal.
     * ============================================================
     */

    const initialVariant = readCurrentVariant();

    currentVariantIsCustomized =
      isCustomizedVariant(initialVariant);

    setDynamicCheckoutVisibility(
      currentVariantIsCustomized
    );

    if (
      currentVariantIsCustomized &&
      measurementsAreSaved()
    ) {
      restoreModalFromStorage();
      enableSavedProperties();
      showSavedStatus();
    } else {
      setPropertiesEnabled(false);
      hideSavedStatus();

      if (measurementsAreSaved()) {
        restoreModalFromStorage();
      } else {
        updateUnitLabels();
      }
    }
  }

  /*
   * ============================================================
   * INITIALIZE
   * ============================================================
   */

  function initAll() {
    document
      .querySelectorAll('[data-custom-size-root]')
      .forEach(initCustomSize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initAll
    );
  } else {
    initAll();
  }

  document.addEventListener(
    'shopify:section:load',
    initAll
  );

  /*
   * Prestige replaces parts of product-info when
   * variants change. Initialize newly inserted roots.
   *
   * IMPORTANT:
   * initCustomSize itself NEVER automatically
   * opens the modal.
   */
  const observer =
    new MutationObserver(() => {
      initAll();
    });

  observer.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );
})();